import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStoresUI } from '@context/StoresContext';
import { filterStoresForPdpPreview } from '_components/Product/FindInStore/filterStoresForPdpPreview';

/** Browser geolocation on the PDP for “km away” in Find in store (does not change favourite store). */
export type PdpShopperGeolocation = { latitude: number; longitude: number };

type UsePdpFindInStorePreviewArgs = {
  isGiftCard: boolean;
};

export const usePdpFindInStorePreview = ({
  isGiftCard,
}: UsePdpFindInStorePreviewArgs) => {
  const { stores, favouriteStoreDetails } = useStoresUI();
  const [shopperGeolocation, setShopperGeolocation] =
    useState<PdpShopperGeolocation | null>(null);
  const previewGeoRequestStartedRef = useRef(false);

  const favouriteStoreId = favouriteStoreDetails?.data?.mec_store_id;
  const hasFavouriteStore = Boolean(
    favouriteStoreDetails && favouriteStoreDetails !== false && favouriteStoreId
  );
  const favouriteStoreForPreview =
    favouriteStoreDetails && favouriteStoreDetails !== false
      ? favouriteStoreDetails
      : undefined;

  const previewStores = useMemo(
    () => filterStoresForPdpPreview(stores, favouriteStoreForPreview),
    [stores, favouriteStoreForPreview]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isGiftCard || !hasFavouriteStore || previewStores.length === 0) return;
    if (shopperGeolocation != null) return;
    if (previewGeoRequestStartedRef.current) return;
    if (!navigator.geolocation) return;

    previewGeoRequestStartedRef.current = true;

    let cancelled = false;

    const requestPosition = () => {
      navigator.geolocation.getCurrentPosition(
        (result) => {
          if (cancelled) return;
          const { latitude, longitude } = result.coords;
          if (
            typeof latitude === 'number' &&
            typeof longitude === 'number' &&
            !Number.isNaN(latitude) &&
            !Number.isNaN(longitude)
          ) {
            setShopperGeolocation({ latitude, longitude });
          }
        },
        () => {},
        { enableHighAccuracy: false, timeout: 20_000, maximumAge: 0 }
      );
    };

    const run = async () => {
      try {
        const perm = await navigator.permissions?.query({
          name: 'geolocation' as PermissionName,
        });
        if (perm?.state === 'denied') return;
      } catch {
        // Permissions API missing — still try geolocation
      }
      requestPosition();
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [hasFavouriteStore, isGiftCard, previewStores.length, shopperGeolocation]);

  const setShopperGeolocationStable = useCallback(
    (location: PdpShopperGeolocation) => {
      setShopperGeolocation(location);
    },
    []
  );

  return {
    shopperGeolocation,
    setShopperGeolocationStable,
  };
};
